/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, CheckCircle, XCircle, Search } from 'lucide-react';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { toLocal } from '../utils/dateTime';

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_MIN_LENGTH = 3;
const PAGE_LIMIT = 10;

const ConfirmBulkImport = () => {
  const { importId } = useParams();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);       // common fields + totals, set once
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const abortRef = useRef(null);

  const fetchRecords = useCallback((page, searchTerm) => {
    // Cancel previous in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    apiClient.get(`/bulk-imports/${importId}/records`, {
      params: { page, limit: PAGE_LIMIT, search: searchTerm },
      signal: controller.signal,
    })
      .then(r => {
        const d = r.data;
        setRecords(d.records);
        setCurrentPage(d.page);
        setTotalPages(d.totalPages);
        // Only set meta once (common fields don't change)
        setMeta(prev => prev ?? {
          workflowName: d.workflowName,
          creatorName: d.creatorName,
          expiresAt: d.expiresAt,
          restrictToSingleSession: d.restrictToSingleSession,
          requiredOtpVerification: d.requiredOtpVerification,
          sendOtpVia: d.sendOtpVia,
          totalRecords: d.totalRecords,
          importStatus: d.importStatus,
        });
        setError('');
      })
      .catch(err => {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setError('Failed to load import records.');
        }
      })
      .finally(() => { setLoading(false); setInitialLoading(false); });
  }, [importId]);

  // Debounce search; reset page to 1 on new search
  useEffect(() => {
    const isSearchTooShort = search.trim().length > 0 && search.trim().length < SEARCH_MIN_LENGTH;
    if (isSearchTooShort) return;

    const timeout = setTimeout(() => {
      fetchRecords(1, search.trim());
      setCurrentPage(1);
    }, search === '' ? 0 : SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search, fetchRecords]);

  // Page change (no debounce needed)
  useEffect(() => {
    const isSearchTooShort = search.trim().length > 0 && search.trim().length < SEARCH_MIN_LENGTH;
    if (isSearchTooShort) return;
    fetchRecords(currentPage, search.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const formatExpiry = (value, unit) => {
    if (!value || !unit) return '—';
    return `${value} ${unit.charAt(0).toUpperCase() + unit.slice(1)}`;
  };

  const formatExpiresAt = (value) => {
    if (!value) return '—';
    return toLocal(value, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      await apiClient.delete(`/bulk-imports/${importId}/cancel`);
      navigate('/assignments/bulk-send');
    } catch (err) {
      setCancelError(err?.response?.data?.message ?? 'Failed to cancel import. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmError('');
    try {
      const response = await apiClient.post(`/bulk-imports/${importId}/confirm`);
      const message = response?.data?.message || 'Import confirmed successfully';
      toastService.success(message);
      navigate('/assignments');
    } catch (err) {
      const message = err?.response?.data?.message ?? 'Failed to confirm import. Please try again.';
      setConfirmError(message);
      toastService.error(message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="flex flex-col h-auto rounded-2xl border border-base-300 shadow-sm bg-base-100">
      {/* Header */}
      <div className="p-6 sm:p-8 border-b border-base-300 flex justify-between items-start flex-shrink-0">
        <div>
          <h1 className="page-title mb-1">Confirm Bulk Import</h1>
          <p className="page-subtitle">Review the records before confirming the import</p>
        </div>
        <button
          onClick={() => setShowCancelModal(true)}
          className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
          title="Close"
        >
          <X size={24} />
        </button>
      </div>

      {(error || confirmError) && (
        <div className="m-8 bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-sm font-bold">
          {error || confirmError}
        </div>
      )}

      {initialLoading && !error && (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <span className="loading loading-spinner loading-lg text-primary" />
          <p className="text-[10px] font-black text-base-content/30 uppercase tracking-widest">Loading records...</p>
        </div>
      )}

      {!initialLoading && !error && (
        <div className="p-6 sm:p-8 flex flex-col gap-6">

          {/* Common fields — shown once meta is loaded */}
          {meta && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 bg-base-200/50 border border-base-300 rounded-xl">
              <div>
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1">Workflow</p>
                <p className="text-sm font-bold text-base-content">{meta.workflowName || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1">Creator Name</p>
                <p className="text-sm font-bold text-base-content">{meta.creatorName || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1">Expires At</p>
                <p className="text-sm font-bold text-base-content">{formatExpiresAt(meta.expiresAt)}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1">Single Session</p>
                <p className="text-sm font-bold text-base-content">{meta.restrictToSingleSession ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] mb-1">OTP Verification</p>
                <p className="text-sm font-bold text-base-content">
                  {meta.requiredOtpVerification ? `Yes (${meta.sendOtpVia === 'sms' ? 'SMS' : 'Email'})` : 'No'}
                </p>
              </div>
            </div>
          )}

          {/* Summary badges */}
          {meta && (
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em]">
                Total: {meta.totalRecords}
              </span>
            </div>
          )}

          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/20" size={16} />
            <input
              type="text"
              placeholder="Search records"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                if (e.target.value === '') setCurrentPage(1);
              }}
              className="w-full bg-base-100 border border-base-content/10 rounded-xl py-2.5 pl-9 pr-9 text-sm focus:outline-none focus:border-primary/50 placeholder:text-base-content/20 shadow-sm transition-all"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/20 hover:text-error"
              >
                <X size={14} />
              </button>
            )}
            {search.trim().length > 0 && search.trim().length < SEARCH_MIN_LENGTH && (
              <div className="absolute top-full left-0 mt-1 text-[10px] text-error font-black uppercase tracking-widest">
                Min 3 characters
              </div>
            )}
          </div>

          {/* Records table */}
          <div className="overflow-x-auto rounded-xl border border-base-300">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-base-200/60">
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3">#</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3 w-72 max-w-[10rem]">Recipient Name</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3 w-72 max-w-[10rem]">Customer Ref No.</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3 w-56">Email</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3 w-72 max-w-[10rem]">Phone</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3">Status</th>
                  <th className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] py-3 w-72">Errors</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16">
                      <span className="loading loading-spinner loading-md text-primary" />
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16 text-[10px] font-black text-base-content/30 uppercase tracking-widest">
                      No records found
                    </td>
                  </tr>
                ) : (
                  records.map((record, i) => (
                    <tr key={record.uid} className="border-t border-base-300 hover:bg-base-200/30 transition-colors">
                      <td className="text-xs text-base-content/40 font-medium py-3">{(currentPage - 1) * PAGE_LIMIT + i + 1}</td>
                      <td className="text-sm font-medium text-base-content py-3 w-72 max-w-[10rem] break-all whitespace-normal">{record.recipientName || '—'}</td>
                      <td className="text-sm font-medium text-base-content py-3 w-72 max-w-[10rem] break-all whitespace-normal">{record.customerReferenceNumber || '—'}</td>
                      <td className="text-sm text-base-content/70 py-3 w-56 max-w-[14rem] break-all whitespace-normal">{record.email || '—'}</td>
                      <td className="text-sm text-base-content/70 py-3 w-72 max-w-[10rem] break-all whitespace-normal">{record.phone || '—'}</td>
                      <td className="py-3">
                        {record.validationStatus === 'success' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-success/10 text-success text-[10px] font-black uppercase tracking-widest">
                            <CheckCircle size={10} /> Valid
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-error/10 text-error text-[10px] font-black uppercase tracking-widest">
                            <XCircle size={10} /> Invalid
                          </span>
                        )}
                      </td>
                      <td className="py-3 w-72">
                        {record.validationErrors?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {record.validationErrors.map((e, idx) => (
                              <span key={idx} className="inline-block px-2 py-0.5 rounded-lg bg-error/8 border border-error/15 text-error/80 text-[10px] font-semibold">
                                {e.message}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-base-content/20 text-[10px]">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            loading={loading}
          />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowCancelModal(true)}
              className="flex-1 min-h-[56px] px-5 rounded-2xl border border-base-300 bg-base-100 text-base-content/70 font-semibold text-sm hover:bg-base-200 transition-all"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!meta || meta.totalRecords === 0 || confirming}
              className="flex-1 min-h-[56px] px-5 rounded-2xl bg-primary text-primary-content font-bold text-sm tracking-wide shadow-sm hover:scale-[1.01] hover:shadow-md transition-all disabled:opacity-50 disabled:hover:scale-100"
            >
              {confirming ? <span className="loading loading-spinner loading-sm" /> : 'Confirm'}
            </button>
          </div>

        </div>
      )}

      <Modal isOpen={showCancelModal} onClose={() => { setShowCancelModal(false); setCancelError(''); }} title="Cancel Import">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-base-content/70">
            Are you sure you want to cancel this import? All uploaded records will be permanently deleted and cannot be recovered.
          </p>
          {cancelError && (
            <p className="text-error text-xs font-bold">{cancelError}</p>
          )}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => { setShowCancelModal(false); setCancelError(''); }}
              className="flex-1 min-h-[44px] px-4 rounded-xl border border-base-300 bg-base-100 text-base-content/70 font-semibold text-sm hover:bg-base-200 transition-all"
            >
              Keep Import
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="flex-1 min-h-[44px] px-4 rounded-xl bg-error text-white font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50"
            >
              {cancelling ? <span className="loading loading-spinner loading-sm" /> : 'Yes, Cancel'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ConfirmBulkImport;
